import { useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, ChevronLeft, ChevronRight } from "lucide-react";

export default function Lightbox({ images, index, onClose, onIndex }) {
  const open = index !== null && index >= 0;

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowRight") onIndex((index + 1) % images.length);
      if (e.key === "ArrowLeft") onIndex((index - 1 + images.length) % images.length);
    };
    window.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [open, index, images, onClose, onIndex]);

  if (!open) return null;
  const cur = images[index];
  const go = (dir) => onIndex((index + dir + images.length) % images.length);

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
        transition={{ duration: 0.3 }}
        data-testid="lightbox"
        className="fixed inset-0 z-[100] bg-[#0F2537]/97 flex items-center justify-center"
        onClick={onClose}
      >
        <button data-testid="lightbox-close" onClick={onClose}
          className="absolute top-6 right-6 text-white/80 hover:text-white transition-colors z-10">
          <X className="w-8 h-8" strokeWidth={1.5} />
        </button>
        <span className="absolute top-8 left-8 overline text-white/60 z-10">{index + 1} / {images.length}</span>

        <button data-testid="lightbox-prev" onClick={(e) => { e.stopPropagation(); go(-1); }}
          className="absolute left-4 lg:left-10 text-white/70 hover:text-white transition-colors z-10 p-2">
          <ChevronLeft className="w-10 h-10" strokeWidth={1} />
        </button>
        <button data-testid="lightbox-next" onClick={(e) => { e.stopPropagation(); go(1); }}
          className="absolute right-4 lg:right-10 text-white/70 hover:text-white transition-colors z-10 p-2">
          <ChevronRight className="w-10 h-10" strokeWidth={1} />
        </button>

        <motion.div
          key={index}
          className="max-w-[90vw] max-h-[85vh] flex flex-col items-center"
          onClick={(e) => e.stopPropagation()}
          drag="x" dragConstraints={{ left: 0, right: 0 }} dragElastic={0.2}
          onDragEnd={(e, info) => { if (info.offset.x < -80) go(1); else if (info.offset.x > 80) go(-1); }}
          initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
        >
          <img src={cur.src} alt={cur.label} draggable={false}
            className="max-w-[90vw] max-h-[78vh] object-contain rounded-sm select-none" />
          {cur.label && <span className="text-white/80 font-display text-xl mt-5">{cur.label}</span>}
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}
